import { RecordingTranslationKeys } from '../models/TranslationKeys';
import { AdultType, SortDir, StreamType } from '../models/Shared.types';
import { timeStringToMs } from '../../Business/utility/DateFormatter';
import { ChannelModel } from '../EpgApi.types';
import { Backend } from '../Backend';

export interface RecordingListResponse {
  subscriptions: SubscriptionContainer;
  recordings?: Recording[];
  status: IRecordingStatus;
}

export interface ISubscription {
  identifier: string;
  device: string;
  version?: number;
  beginBuffer: number;
  endBuffer: number;
  deleteProtected: boolean;
  channel: string;
}

export interface ISeriesSubscription extends ISubscription {
  series: string;
  excludedSeasons: string[];
  excludedPrograms: string[];
  excludedSchedules: string[];
}

export interface ISeasonSubscription extends ISubscription {
  season: string;
  excludedPrograms: string[];
  excludedSchedules: string[];
}

export interface IProgramSubscription extends ISubscription {
  program: string;
  excludedSchedules: string[];
}

export interface SubscriptionContainer {
  seriesSubscriptions: ISeriesSubscription[];
  seasonSubscriptions: ISeasonSubscription[];
  programSubscriptions: IProgramSubscription[];
}

export interface IRecordingStatus {
  npvr?: INpvrStatus;
}

export interface INpvrStatus {
  maximumHours?: number;
  usedHours?: number;
}

export interface RecordingSpace {
  available: string;
  used: number;
  max: number;
}

export const enum RecordingPageTypes {
  recorded = 'recorded',
  planned = 'planned',
  conflict = 'conflict',
  expires = 'expires',
}

export const enum InfernoState {
  failed = 'FAILED',
  contentDeleted = 'CONTENT_DELETED',
  unresolved = 'UNRESOLVED',
  inProgressWithErrorNoSignal = 'IN_PROGRESS_WITH_ERROR_NO_SIGNAL',
  inProgressWithErrorWasBeingRecorded = 'IN_PROGRESS_WITH_ERROR_WAS_BEING_RECORDED',
  inProgressWithErrorWasBeingRecordedNoSignal = 'IN_PROGRESS_WITH_ERROR_WAS_BEING_RECORDED_NO_SIGNAL',
  pendingWithConflict = 'PENDING_WITH_CONFLICT',
  valueInProgressWithError = 'VALUE_IN_PROGRESS_WITH_ERROR',
}

export const enum InfernoFailureReason {
  insufficient_resources = 'INSUFFICIENT_RESOURCES',
}

export const enum RecordingIcons {
  folder = './images/recording/recording-folder.png',
  planned = './images/recording/recording-planned.png',
  inProgress = './images/recording/recording-inprogress.png',
  recorded = './images/recording/recording-recorded.png',
  error = './images/recording/recording-error.png',
  inProgressError = './images/recording/recording-error.png',
  inProgressConflict = './images/recording/recording-inprogress-conflict.png',
  plannedConflict = './images/recording/recording-conflict.png',
  locked = './images/recording/recording-locked.png',
}

export class IRecordingBase {
  identifier: string;
  device: string;
  version?: number;
  schedule: string;
  program: string;
  programOrdinal?: number;
  season?: string;
  seasonOrdinal?: number;
  series?: string;
  channel: string;
  start: string;
  end: string;
  plannedStart: string;
  plannedEnd: string;
  effectiveStart?: string;
  effectiveEnd?: string;
  beginBuffer?: number;
  endBuffer?: number;
  deleteProtected?: boolean;
  infernoIdentifier?: string;
  infernoState?: string;
  infernoFailureReason?: string;
  infernoSize?: number;
  programSubscription?: string;
  seasonSubscription?: string;
  seriesSubscription?: string;
  conflicts?: string[];
  bookmark?: number;
  adult?: boolean;
  expiration?: string;

  constructor(recording: Recording) {
    this.identifier = recording.identifier;
    this.device = recording.device;
    this.version = recording.version;
    this.schedule = recording.schedule;
    this.program = recording.program;
    this.programOrdinal = recording.programOrdinal;
    this.season = recording.season;
    this.seasonOrdinal = recording.seasonOrdinal;
    this.series = recording.series;
    this.channel = recording.channel;
    this.start = recording.start;
    this.end = recording.end;
    this.plannedStart = recording.plannedStart;
    this.plannedEnd = recording.plannedEnd;
    this.effectiveStart = recording.effectiveStart;
    this.effectiveEnd = recording.effectiveEnd;
    this.beginBuffer = recording.beginBuffer;
    this.endBuffer = recording.endBuffer;
    this.deleteProtected = recording.deleteProtected;
    this.infernoIdentifier = recording.infernoIdentifier;
    this.infernoState = recording.infernoState;
    this.infernoFailureReason = recording.infernoFailureReason;
    this.infernoSize = recording.infernoSize;
    this.programSubscription = recording.programSubscription;
    this.seasonSubscription = recording.seasonSubscription;
    this.seriesSubscription = recording.seriesSubscription;
    this.conflicts = recording.conflicts;
    this.adult = recording.adult;
    this.expiration = recording.expiration;
  }
}

export class Recording extends IRecordingBase {
  expireBuffer: number;

  constructor(recording: Recording, expireBuffer: number) {
    super(recording);
    this.expireBuffer = expireBuffer;
  }

  isPlanned() {
    const now = new Date().getTime();

    const start = timeStringToMs(this.plannedStart);
    const begin = this.beginBuffer || 0; // if no beginbuffer is set, default to 0

    return start - begin >= now;
  }

  isInProgress() {
    const now = new Date().getTime();

    const end = timeStringToMs(this.plannedEnd);
    const start = timeStringToMs(this.plannedStart);
    const begin = this.beginBuffer || 0; // if no beginbuffer is set, default to 0
    const endbuffer = this.endBuffer || 0;

    return end + endbuffer > now && start - begin < now;
  }

  isRecorded() {
    const now = new Date().getTime();

    const end = timeStringToMs(this.plannedEnd);
    const endbuffer = this.endBuffer || 0;

    return end + endbuffer <= now;
  }

  hasConflict() {
    if (this.infernoState === InfernoState.pendingWithConflict) {
      return true;
    } else if (
      this.infernoState === InfernoState.valueInProgressWithError &&
      this.infernoFailureReason === InfernoFailureReason.insufficient_resources
    ) {
      return true;
    }
    return false;
  }

  isAboutToExpire() {
    const expires = new Date();
    const interval = this.expireBuffer;
    expires.setDate(expires.getDate() + interval);
    return timeStringToMs(this.expiration) < expires.getTime();
  }

  duration() {
    return timeStringToMs(this.plannedEnd) - timeStringToMs(this.plannedStart);
  }

  totalDuration() {
    const beginBuffer = this.beginBuffer || 0; // if no beginbuffer is set, default to 0
    const endBuffer = this.endBuffer || 0;
    return this.duration() + beginBuffer + endBuffer;
  }

  hasError() {
    if (
      this.infernoState === InfernoState.failed ||
      this.infernoState === InfernoState.contentDeleted ||
      this.infernoState === InfernoState.unresolved
    ) {
      return true;
    }
    return this.inProgressWithError();
  }

  inProgressWithError() {
    return (
      this.infernoState === InfernoState.inProgressWithErrorNoSignal ||
      this.infernoState === InfernoState.inProgressWithErrorWasBeingRecorded ||
      this.infernoState === InfernoState.inProgressWithErrorWasBeingRecordedNoSignal
    );
  }

  canWatchNpvrWithRights(channel: ChannelModel): boolean {
    return channel.customerHasRight(StreamType.NPVR) && (this.isRecorded() || this.isInProgress());
  }
}

export class RecordingsCache {
  private _recordings: Recording[] = [];
  private _programSubscriptions: IProgramSubscription[] = [];
  private _seriesSubscriptions: ISeriesSubscription[] = [];
  private _seasonSubscriptions: ISeasonSubscription[] = [];
  private _status?: IRecordingStatus = undefined;

  set recordings(val: Recording[]) {
    this._recordings = val;
  }

  get recordings(): Recording[] {
    return this._recordings;
  }

  set subscriptions(val: SubscriptionContainer) {
    this._programSubscriptions = val.programSubscriptions;
    this._seriesSubscriptions = val.seriesSubscriptions;
    this._seasonSubscriptions = val.seasonSubscriptions;
  }

  set status(val: IRecordingStatus) {
    this._status = val;
  }

  availableUsedMaxSpace(backend: Backend): RecordingSpace {
    let availableSpace = '';
    let usedSpace = 0;
    let maxSpace = 0;
    if (
      this._status &&
      this._status.npvr &&
      this._status.npvr.maximumHours !== undefined &&
      this._status.npvr.usedHours !== undefined
    ) {
      usedSpace = this._status.npvr.usedHours;
      maxSpace = this._status.npvr.maximumHours;
      let availableHours = this._status.npvr.maximumHours - this._status.npvr.usedHours;
      if (availableHours < 1) {
        const availableMinutes = Math.floor(60 * availableHours);
        availableSpace = backend.translations
          .forKey(RecordingTranslationKeys.recordings_minutes_available)
          .replace('$1', availableMinutes.toString())
          .replace('$2', this._status.npvr.maximumHours.toString());
      } else {
        availableHours = Math.max(Math.floor(availableHours), 0);
        availableSpace = backend.translations
          .forKey(RecordingTranslationKeys.recordings_hours_available)
          .replace('$1', availableHours.toString())
          .replace('$2', this._status.npvr.maximumHours.toString());
      }
    }
    return {
      available: availableSpace,
      used: usedSpace,
      max: maxSpace,
    };
  }

  programSubscription(programId: string, channelId: string): IProgramSubscription | undefined {
    if (this._programSubscriptions === undefined) {
      return undefined;
    }
    return this._programSubscriptions.find(sub => sub.identifier === programId && sub.channel === channelId);
  }

  seasonSubscription(seasonId: string, channelId: string): ISeasonSubscription | undefined {
    if (this._seasonSubscriptions === undefined) {
      return undefined;
    }
    return this._seasonSubscriptions.find(sub => sub.identifier === seasonId && sub.channel === channelId);
  }

  seriesSubscription(seriesId: string, channelId: string): ISeriesSubscription | undefined {
    if (this._seriesSubscriptions === undefined) {
      return undefined;
    }
    return this._seriesSubscriptions.find(sub => sub.identifier === seriesId && sub.channel === channelId);
  }

  getRecordings(allowAdult = AdultType.Any) {
    if (allowAdult) {
      return this._recordings;
    } else {
      return this._recordings.filter(recording => {
        return !recording.adult;
      });
    }
  }

  recordingsForType(type: RecordingPageTypes, showAdult: AdultType) {
    switch (type) {
      case RecordingPageTypes.recorded:
        return this.finishedRecordings(showAdult);
      case RecordingPageTypes.planned:
        return this.plannedRecordings(showAdult);
      case RecordingPageTypes.conflict:
        return this.conflictRecordings(showAdult);
      case RecordingPageTypes.expires:
        return this.expiredRecordings(showAdult);
      default:
        return [];
    }
  }

  sortedRecordingsOnExpirationTime(dir: SortDir = SortDir.Asc): Recording[] {
    return this._recordings.sort((a, b) => {
      if (parseInt(<string>a.expiration) < parseInt(<string>b.expiration)) {
        return dir === SortDir.Asc ? -1 : 1;
      } else if (parseInt(<string>a.expiration) > parseInt(<string>b.expiration)) {
        return dir === SortDir.Asc ? 1 : -1;
      } else {
        if (parseInt(a.plannedStart) < parseInt(b.plannedStart)) {
          return dir === SortDir.Asc ? -1 : 1;
        } else if (parseInt(a.plannedStart) > parseInt(b.plannedStart)) {
          return dir === SortDir.Asc ? 1 : -1;
        } else {
          return 0;
        }
      }
    });
  }

  sortedRecordingsOnStartTime(dir = SortDir.Asc) {
    return this._recordings.sort((a, b) => {
      if (parseInt(a.plannedStart) < parseInt(b.plannedStart)) {
        return dir === SortDir.Asc ? -1 : 1;
      } else if (parseInt(a.plannedStart) > parseInt(b.plannedStart)) {
        return dir === SortDir.Asc ? 1 : -1;
      } else {
        if (parseInt(a.plannedEnd) < parseInt(b.plannedEnd)) {
          return dir === SortDir.Asc ? -1 : 1;
        } else if (parseInt(a.plannedEnd) > parseInt(b.plannedEnd)) {
          return dir === SortDir.Asc ? 1 : -1;
        } else {
          return 0;
        }
      }
    });
  }

  sortedRecordingsOnEndTime(dir = SortDir.Desc) {
    return this._recordings.sort((a, b) => {
      if (parseInt(a.plannedEnd) < parseInt(b.plannedEnd)) {
        return dir === SortDir.Asc ? -1 : 1;
      } else if (parseInt(a.plannedEnd) > parseInt(b.plannedEnd)) {
        return dir === SortDir.Asc ? 1 : -1;
      } else {
        if (parseInt(a.plannedStart) < parseInt(b.plannedStart)) {
          return dir === SortDir.Asc ? -1 : 1;
        } else if (parseInt(a.plannedStart) > parseInt(b.plannedStart)) {
          return dir === SortDir.Asc ? 1 : -1;
        } else {
          return 0;
        }
      }
    });
  }

  plannedRecordings(showAdult = AdultType.Any) {
    if (this._recordings.length === 0) {
      return [];
    }
    const resultRecordings = this.sortedRecordingsOnStartTime(SortDir.Asc).filter(recording => {
      return recording.isPlanned();
    });
    return this.filterAdult(resultRecordings, showAdult);
  }

  finishedRecordings(showAdult = AdultType.Any) {
    if (this._recordings.length === 0) {
      return [];
    }
    const resultRecordings = this.sortedRecordingsOnEndTime(SortDir.Desc).filter(recording => {
      return recording.isRecorded() || recording.isInProgress();
    });
    return this.filterAdult(resultRecordings, showAdult);
  }

  conflictRecordings(showAdult = AdultType.Any) {
    if (this._recordings.length === 0) {
      return [];
    }

    const resultRecordings = this.sortedRecordingsOnStartTime(SortDir.Asc).filter(recording => {
      return recording.hasConflict();
    });
    return this.filterAdult(resultRecordings, showAdult);
  }

  expiredRecordings(showAdult = AdultType.Any) {
    if (this._recordings.length === 0) {
      return [];
    }
    const resultRecordings = this.sortedRecordingsOnExpirationTime(SortDir.Asc).filter(recording => {
      return recording.isAboutToExpire();
    });

    return this.filterAdult(resultRecordings, showAdult);
  }

  filterAdult(recordings: Recording[], showAdult: AdultType): Recording[] {
    if (showAdult === AdultType.True || showAdult === AdultType.Any) {
      return recordings;
    } else {
      return recordings.filter(recording => {
        return !recording.adult;
      });
    }
  }
}
